import { _ as _asyncToGenerator, f as fetchData, d as defineElement } from './bundle_chunk_utils_0868241c.js';

/******************************************************
 * Screen Reader Status
 ******************************************************/

/**
 * Allows a message to be announced by screen readers.
 * This should be used whenever a change happens dynamically
 * on the page that is not automatically announces to the user.
 * An example would be adding a product to the ajax cart - sighted users
 * can see the drawer open and the item in the cart, but non-sighted
 * users wouldn't know if the cart add was successful. This allows
 * users to have an audio announcement of what happened.
 *
 * @param {String} message - The message to allow screen readers to announce
 */
var updateA11yStatus = message => {
  var screenReaderStatus = document.getElementById("screenReaderStatus");
  screenReaderStatus.textContent = "";
  screenReaderStatus.textContent = message;
};

/******************************************************
 * Global Variables
 ******************************************************/

var classes = {
  loading: "loading"
};
var selectors = {
  form: ".js-filters",
  input: ".js-filters-input",
  priceMin: "[name='filter.v.price.gte']",
  priceMax: "[name='filter.v.price.lte']",
  resultsCount: ".js-filters-results-count",
  filtersGroup: ".js-filters-group",
  removeActive: ".js-filters-active",
  clearAll: ".js-filters-clear-all",
  clearGroup: ".js-filters-clear-group",
  dropdownToggle: ".js-filters-dropdown-toggle",
  // dropdownApply: ".js-filters-dropdown-apply",
  activeFiltersCount: ".js-filters-active-count"
};
var collectionProducts = document.getElementById("collectionProducts");
var filtersForm = document.getElementById("collectionFilters");
var activeFiltersElement = document.getElementById("collectionFiltersActive"); // This should be the ID of the section whose content will be
// totally replaced with the results of the filtering. Note
// that it should contain the pagination logic.

var replaceContentID$1 = "collectionProducts";
var filterDataID = "filterData";
var activeFilters = "collectionFiltersActive";
var initialURL = new URL(window.location.href);
/******************************************************
 * DOM Updates
 ******************************************************/

/**
 * Fetches results for the currently-selected
 * filters, and updates the DOM with the results.
 *
 * @param {String} urlHREF The URL to fetch
 */

var updateProductGrid = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator(function* (urlHREF) {
    // Set loading state
    setLoadingState(true); // Fetch filters results and update markup

    var response = yield fetchData(urlHREF, undefined, false);
    var updatedProducts = replaceProductMarkup(response, urlHREF); // Get JSON data from results

    var data = JSON.parse(updatedProducts.querySelector("#".concat(filterDataID)).textContent); // Update DOM with various types of data

    updateDomData(data); // Remove loading state

    setLoadingState(false); // Reset initial URL

    initialURL = new URL(window.location.href); // Announce change for screen readers

    var productText = "products";
    if (data.totalResults === 1) productText = "product";
    updateA11yStatus("".concat(data.totalResults, " ").concat(productText, " found"));
  });

  return function updateProductGrid(_x) {
    return _ref.apply(this, arguments);
  };
}();
/**
 * Gets initial filter data including
 * the count of currently-active filters,
 * as well as the number of results, and updates
 * the DOM with that data on initial page load.
 */


var setInitialData = () => {
  var data = document.getElementById(filterDataID);
  if (!data) return;
  data = JSON.parse(document.getElementById(filterDataID).textContent);
  updateDomData(data);
};
/**
 * Pulls content from the filter result so it's
 * ready to be inserted into the DOM.
 *
 * @param {Object} response The response to the fetch request
 * @param {String} url The URL used for the fetch request
 * @returns {String} The new content ready to be inserted into the DOM
 */


var replaceProductMarkup = (response, url) => {
  var parser = new DOMParser();
  var fetchedDocument = parser.parseFromString(response, "text/html");
  var updatedProducts = fetchedDocument.documentElement.querySelector("#".concat(replaceContentID$1));
  document.getElementById(replaceContentID$1).innerHTML = updatedProducts.innerHTML;
  window.history.pushState({}, "", url);
  var updatedActiveFilters = fetchedDocument.documentElement.querySelector("#".concat(activeFilters));
  activeFiltersElement.innerHTML = updatedActiveFilters.innerHTML;
  return updatedProducts;
};
/**
 * Sets or removes the loading state.
 *
 * @param {Boolean} loading Whether or not we're in a loading state
 */


var setLoadingState = loading => {
  if (loading) {
    collectionProducts.classList.add(classes.loading);
    filtersForm.classList.add(classes.loading);
  }

  if (!loading) {
    collectionProducts.classList.remove(classes.loading);
    filtersForm.classList.remove(classes.loading);
  }
};
/**
 * Updates the DOM with data pulled from
 * the current filtered view. Helpful in places
 * where content should be updated, but is outside
 * of the content that is replaced by the filtering.
 *
 * @param {Node} data The script tag with JSON data on the filtered view result
 */


var updateDomData = data => {
  var resultsNumbers = [...document.querySelectorAll(selectors.resultsCount)];
  var activeFiltersCount = [...document.querySelectorAll(selectors.activeFiltersCount)]; // Update total count of results

  for (var count of resultsNumbers) {
    count.textContent = data.totalResults;
  } // Update total count of active filters


  for (var _count of activeFiltersCount) {
    data.activeValues > 0 ? _count.textContent = "(".concat(data.activeValues, ")") : _count.textContent = "";
  }

  if (data.activeValues === 0) {
    activeFiltersElement.classList.add('hidden');
  } else {
    activeFiltersElement.classList.remove('hidden');
  }

  var clearAll = filtersForm.querySelector(selectors.clearAll);

  if (data.activeValues === 0) {
    clearAll.classList.add('hidden');
  } else {
    clearAll.classList.remove('hidden');
  }
};
/******************************************************
 * Get URLS
 ******************************************************/

/**
 * Modifies the URL to be used with the
 * fetch request based on radio input selection.
 *
 * @param {Node} option The radio input
 * @returns {String} The updated URL
 */


var getUrlFromRadio = option => {
  var url = initialURL; // with radio buttons, only one option with the name should be applied
  // therefore delete all, then if something is checked, apply filter accordingly

  url.searchParams.delete(option.name); // In case we're on a page other than the first, remove
  // that parameter so that pagination will start over (in the
  // case of results being less than one page)

  url.searchParams.delete("page");

  if (option.checked) {
    url.searchParams.set(option.name, option.value);
  }

  return url;
};
/**
 * Modifies the URL to be used with the
 * fetch request based on checkbox input selection.
 *
 * @param {Node} option The checkbox input
 * @returns {String} The updated URL
 */


var getUrlFromCheckbox = option => {
  var url = initialURL;
  var name = option.name;
  var value = option.value; // In case we're on a page other than the first, remove
  // that parameter so that pagination will start over (in the
  // case of results being less than one page)

  url.searchParams.delete("page");

  if (option.checked) {
    url.searchParams.append(name, value);
  } else {
    // only remove the specific name/value pair
    // https://stackoverflow.com/questions/65241446/how-to-remove-only-one-of-multiple-key-value-pairs-with-the-same-key-using-urlse
    var targetParams = url.searchParams.getAll(name).filter(name => name != value);
    url.searchParams.delete(name);

    for (var param of targetParams) {
      url.searchParams.append(name, param);
    }
  }

  return url;
};
/******************************************************
 * Clearing Filters
 ******************************************************/

/**
 * Clears one group of filters
 *
 * @param {Event} e The click event
 */


var clearGroup = e => {
  e.preventDefault();
  var link = e.target;
  var group = link.closest(selectors.filtersGroup);
  if (!group) return; // Clear group values

  clearValues(group); // Fetch results

  var url = new URL(window.location.href);
  var firstInput = group.querySelector(selectors.input);
  var maxPrice = group.querySelector(selectors.priceMax);
  url.searchParams.delete(firstInput.name);
  if (maxPrice) url.searchParams.delete(maxPrice.name);
  updateProductGrid(url.href);
};
/**
 * Clears all current filters
 *
 * @param {Event} e The click event
 */


var clearAll = e => {
  e.preventDefault(); // Clear all values

  clearValues(document); // Fetch results

  var url = new URL(window.location.origin + window.location.pathname);
  updateProductGrid(url.href);
};
/**
 * Clears (re-sets) all currently active
 * filters within a given parent.
 *
 * @param {Node} parent The group whose filters should be re-set
 */


var clearValues = parent => {
  // Uncheck options
  for (var option of parent.querySelectorAll("".concat(selectors.input, ":checked"))) {
    option.checked = false;
  } // Reset price values


  for (var _option of parent.querySelectorAll("".concat(selectors.input).concat(selectors.priceMin))) {
    _option.value = "";
  }

  for (var _option2 of parent.querySelectorAll("".concat(selectors.input).concat(selectors.priceMax))) {
    _option2.value = "";
  }
};
/******************************************************
 * Handling Various Input Types
 ******************************************************/

/**
 * Handles radio or checkbox filters
 * and updates the product grid with the results
 *
 * @param {Node} option A radio or checkbox input
 */


var handleRadioCheckbox = option => {
  var group = option.closest(selectors.filtersGroup);
  group.classList.remove('open'); // Get URL that will be used to fetch filter results

  var url = false;

  if (option.type == "radio") {
    url = getUrlFromRadio(option);
  }

  if (option.type == "checkbox") {
    url = getUrlFromCheckbox(option);
  } // URL creation was successful, update the product grid


  if (url) {
    updateProductGrid(url.href);
  }
};
/******************************************************
 * Event Listener Callbacks
 ******************************************************/

/**
 * Remove active filters.
 *
 * @param {Event} e The click event
 */


var removeActive = e => {
  e.preventDefault();
  var active = e.target;
  var name = active.dataset.name;
  var value = active.dataset.value;
  var input = document.querySelector(".js-filters-input[name=\"".concat(name, "\"][value=\"").concat(value, "\"]"));
  input.checked = false;
  var url = getUrlFromCheckbox(input); // URL creation was successful, update the product grid

  if (url) {
    updateProductGrid(url.href);
  }
};
/**
 * Filters collection products either
 * for radio, checkbox, or price filters.
 *
 * @param {Event} e The change event
 */


var filterWithInputs = e => {
  var option = e.target;
  var form = option.closest(selectors.form);
  var type = option.type; // Only continue if we're in the filters
  // form, and the input has a definitive type

  if (!form || !type) return; // Handle checkboxes and radios (standard filters)

  if (type == "radio" || type == "checkbox") {
    handleRadioCheckbox(option);
  }
};
/**
 * Toggle filters group
 *
 * @param {Event} e The click event
 */


var dropdownToggle = e => {
  var filtersGroups = [...filtersForm.querySelectorAll(selectors.filtersGroup)];
  var group = e.target.closest(selectors.filtersGroup);
  filtersGroups.forEach(element => {
    if (element === group) {
      group.classList.toggle('open');
    } else {
      element.classList.remove('open');
    }
  });
};
/**
 * Clears either all filters,
 * or a specific set of filters.
 *
 * @param {Event} e The click event
 */


var handleClick = e => {
  var isClearAllLink = e.target.closest(selectors.clearAll);
  var isClearGroupLink = e.target.closest(selectors.clearGroup);
  var isRemoveActive = e.target.closest(selectors.removeActive);
  var isDropdownToggle = e.target.closest(selectors.dropdownToggle); // const isDropdownApply = e.target.closest(selectors.dropdownApply)

  if (isClearAllLink) clearAll(e);
  if (isClearGroupLink) clearGroup(e);
  if (isRemoveActive) removeActive(e);
  if (isDropdownToggle) dropdownToggle(e);
};
/******************************************************
 * Initialization
 ******************************************************/


if (filtersForm) {
  setInitialData();
  filtersForm.addEventListener("change", filterWithInputs);
  document.addEventListener("click", handleClick); // filtersForm.addEventListener("keydown", handlePriceTyping)
}

/******************************************************
 * Global Variables
 ******************************************************/

var replaceContentID = "collectionProducts";
/******************************************************
 * Class
 ******************************************************/

class CollectionSort extends HTMLElement {
  constructor() {
    super();
    this.sort = this;
    this.select = this.sort.querySelector("select");
  }

  connectedCallback() {
    this.select.addEventListener("change", this._sortCollection.bind(this));
  }

  _sortCollection(e) {
    var value = e.target.value;
    var url = new URL(window.location.href);
    url.searchParams.set("sort_by", value);
    fetch(url.href).then(response => response.text()).then(responseText => {
      var parser = new DOMParser();
      var fetchedDocument = parser.parseFromString(responseText, "text/html");
      var updatedProducts = fetchedDocument.documentElement.querySelector("#".concat(replaceContentID));
      document.querySelector("#".concat(replaceContentID)).innerHTML = updatedProducts.innerHTML;
      window.history.pushState({}, "", url.href);
    });
  }

}
/******************************************************
 * Initialization
 ******************************************************/


defineElement("collection-sort", CollectionSort);

/**
 * This file is to add any JS or components that are specific to this template file
 * by default it is empty.
 */
/**
 * Import DrawerHandler, then initialize all drawers
 */

import('./bundle_chunk_DrawerHandler_56c83219.js').then(_ref => {
  var {
    DrawerHandler
  } = _ref;
  new DrawerHandler({
    drawerName: 'collection-filters',
    drawerSelector: '.js-collection-filters',
    triggerSelector: '.js-collection-filters__toggle',
    overlaySelector: '.js-collection-filters__overlay',
    panelSelector: '.js-collection-filters__panel'
  });
  document.addEventListener('collection-filters-drawer-open', () => {
    var sticky = document.querySelector('.sticky');
    sticky.classList.add('z-10');
    sticky.classList.remove('z-30');
  });
  document.addEventListener('collection-filters-drawer-close', () => {
    var sticky = document.querySelector('.sticky');
    setTimeout(function () {
      sticky.classList.remove('z-10');
      sticky.classList.add('z-30');
    }, 300);
  });
});
